import * as Dialog from "@radix-ui/react-dialog";
import { HTMLProps, PropsWithChildren } from "react";
import clsx from "clsx";

const Root = ({
  children,
  ...rest
}: HTMLProps<HTMLDivElement> & { defaultOpen?: boolean; onOpenChange?: (v: boolean) => void }) => {
  return <Dialog.Root {...rest}>{children}</Dialog.Root>;
};

const Trigger = ({ children, disabled = false }: PropsWithChildren & { disabled?: boolean }) => {
  return (
    <Dialog.Trigger disabled={disabled} className="disabled:cursor-not-allowed">
      {children}
    </Dialog.Trigger>
  );
};

type ModalBodyProps = {
  titleSlot?: React.ReactNode;
  title?: string;
  className?: string;
  contentClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  hideClose?: boolean;
  disableClose?: boolean;
};
const Body = ({
  title,
  titleSlot,
  children,
  className,
  contentClassName,
  titleClassName,
  descriptionClassName,
  hideClose = false,
  disableClose = false,
}: PropsWithChildren<ModalBodyProps>) => {
  return (
    <Dialog.Portal>
      <Dialog.Overlay className={clsx("z-50 bg-neutral-800/50 fixed inset-0", className)}>
        <Dialog.Content
          className={clsx(
            "z-50 fixed flex flex-col",
            "max-md:top-0 max-md:left-0 max-md:right-0 max-md:bottom-0 max-md:w-screen! max-md:h-screen!",
            "md:top-[50%] md:left-[50%] md:translate-x-[-50%] md:translate-y-[-50%]",
            "md:w-[90vw] md:max-w-[800px] lg:max-w-[1200px]",
            "md:rounded-lg bg-white focus:outline-hidden",
            contentClassName
          )}
          data-testid="modal-v2-content"
          onInteractOutside={(e) => disableClose && e.preventDefault()}
          onEscapeKeyDown={(e) => disableClose && e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
        >
          <Dialog.Title
            className={clsx(
              "flex items-center justify-between text-lg",
              {
                "pt-6 px-6 font-medium rounded-sm h-16": !titleClassName,
              },
              titleClassName
            )}
          >
            {titleSlot || <div data-testid="modal-v2-title">{title}</div>}
            {!hideClose && (
              <Dialog.Close className="cursor-pointer">
                <i className="text-2xl icon-lt-close-line" />
              </Dialog.Close>
            )}
          </Dialog.Title>
          <Dialog.Description asChild={true}>
            <div
              className={clsx(
                "text-sm p-6 overflow-y-auto max-h-[calc(100%-64px)] md:max-h-[85vh]",
                descriptionClassName
              )}
            >
              {children}
            </div>
          </Dialog.Description>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog.Portal>
  );
};

export const ModalV2 = {
  Root: Root,
  Trigger: Trigger,
  Body: Body,
};
